import { forwardRef } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseTableFooterBarProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

export const BaseTableFooterBar = forwardRef<
  HTMLDivElement,
  BaseTableFooterBarProps
>(function BaseTableFooterBar(
  {
    appearance,
    leading,
    actions,
    attached = true,
    children,
    className,
    classNames,
    style,
    ...footerBarProps
  },
  ref,
) {
  if (typeof attached !== 'boolean') {
    throw new Error('BaseTableFooterBar attached must be a boolean.');
  }
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);

  return (
    <div
      {...componentAttributes}
      {...footerBarProps}
      ref={ref}
      data-monkeys-component="table-footer-bar"
      data-slot="footer-bar"
      data-base-table-footer-bar=""
      data-attached={attached ? '' : undefined}
      className={cn(
        theme.slots.tableFooterBar,
        attached && theme.slots.tableFooterBarAttached,
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.tableFooterBar,
        ...style,
      }}
    >
      {hasRenderableNode(leading) ? (
        <div className={cn(theme.slots.tableFooterBarLeading, classNames?.leading)}>
          {leading}
        </div>
      ) : null}

      {hasRenderableNode(children) ? (
        <div className={cn(theme.slots.tableFooterBarContent, classNames?.content)}>
          {children}
        </div>
      ) : null}

      {hasRenderableNode(actions) ? (
        <div className={cn(theme.slots.tableFooterBarActions, classNames?.actions)}>
          {actions}
        </div>
      ) : null}
    </div>
  );
});
